import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Form, Alert, Table, Button, Modal, Badge } from 'react-bootstrap';
import {
  queryLogs,
  deleteLogEvent,
  bulkCreateLogs,
  createLogEvent,
  getVideoSessions,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/helpers';
import LoadingSpinner from '../components/LoadingSpinner';

const ACTIONS = [
  'play', 'pause', 'seek',
  'start_record', 'stop_record',
  'upload_start', 'upload_success', 'upload_fail',
  'append_start', 'append_success', 'append_fail',
  'append_record',
];

const PLAYBACK = new Set(['play', 'pause', 'seek']);
const NARRATOR_LABEL = { Won: 'ว่าน', Waan: 'หวาน' };

const emptyBulkRow = () => ({ action: 'play', offset_seconds: '', value: '' });

// A <input type="datetime-local"> value is *local* wall-clock time with no zone.
// The rows are stored (and shown) as UTC, so the insert form converts on submit —
// see insertUtc below.
const toLocalInput = (date) => {
  const pad = (n, width = 2) => String(n).padStart(width, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    + `.${pad(date.getMilliseconds(), 3)}`;
};

/**
 * Admin event-log browser: filter, per-row delete, bulk-add playback markers.
 */
const EventLogPage = () => {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const videoId = searchParams.get('video_id') || '';
  const action = searchParams.get('action') || '';
  const narrator = searchParams.get('narrator') || '';
  const timestampGte = searchParams.get('timestamp_gte') || '';
  const timestampLte = searchParams.get('timestamp_lte') || '';
  const idGte = searchParams.get('id_gte') || '';
  const idLte = searchParams.get('id_lte') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const perPage = 50;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [showBulk, setShowBulk] = useState(false);
  const [bulkVideoId, setBulkVideoId] = useState('');
  const [bulkNarrator, setBulkNarrator] = useState('Won');
  const [bulkSessions, setBulkSessions] = useState([]);
  const [bulkStartId, setBulkStartId] = useState('');
  const [bulkRows, setBulkRows] = useState([emptyBulkRow()]);
  const [bulkReprocess, setBulkReprocess] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [showInsert, setShowInsert] = useState(false);
  const [insertVideoId, setInsertVideoId] = useState('');
  const [insertAction, setInsertAction] = useState('stop_record');
  const [insertNarrator, setInsertNarrator] = useState('');
  const [insertValue, setInsertValue] = useState('');
  const [insertLocal, setInsertLocal] = useState('');
  const [insertBusy, setInsertBusy] = useState(false);
  const [insertResult, setInsertResult] = useState(null);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === '' || value == null) next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setError(null);
    const params = { skip: (page - 1) * perPage, limit: perPage };
    if (videoId) params.video_id = parseInt(videoId, 10);
    if (action) params.action = action;
    if (narrator) params.narrator = narrator;
    if (timestampGte) params.timestamp_gte = new Date(timestampGte).toISOString();
    if (timestampLte) params.timestamp_lte = new Date(timestampLte).toISOString();
    if (idGte) params.id_gte = parseInt(idGte, 10);
    if (idLte) params.id_lte = parseInt(idLte, 10);
    try {
      const res = await queryLogs(params);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
      setError(`โหลดล็อกไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, videoId, action, narrator, timestampGte, timestampLte, idGte, idLte]);

  useEffect(() => {
    load();
  }, [load]);

  const doDelete = async (log) => {
    setDeletingId(log.id);
    setError(null);
    try {
      await deleteLogEvent(log.id);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      setError(`ลบล็อกไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setDeletingId(null);
    }
  };

  const onDeleteClick = (log) => {
    if (PLAYBACK.has(log.action)) {
      doDelete(log);
    } else {
      setConfirmDelete(log);
    }
  };

  const openBulk = () => {
    setBulkResult(null);
    setBulkRows([emptyBulkRow()]);
    setBulkReprocess(true);
    setBulkStartId('');
    setBulkSessions([]);
    setBulkVideoId(videoId || '');
    setShowBulk(true);
  };

  const openInsert = () => {
    setInsertResult(null);
    setInsertVideoId(videoId || '');
    setInsertAction(action || 'stop_record');
    setInsertNarrator(narrator || '');
    setInsertValue('');
    setInsertLocal(toLocalInput(new Date()));
    setShowInsert(true);
  };

  // What will actually be stored, or null while the field is empty/half-typed.
  const parsedInsertAt = insertLocal ? new Date(insertLocal) : null;
  const insertUtc = parsedInsertAt && !Number.isNaN(parsedInsertAt.getTime())
    ? parsedInsertAt.toISOString()
    : null;

  const submitInsert = async () => {
    const id = parseInt(insertVideoId, 10);
    if (!Number.isInteger(id)) {
      setError('กรุณาใส่หมายเลขวิดีโอ');
      return;
    }
    if (!insertUtc) {
      setError('เวลาไม่ถูกต้อง');
      return;
    }
    setInsertBusy(true);
    setInsertResult(null);
    setError(null);
    try {
      const res = await createLogEvent({
        video_id: id,
        action: insertAction,
        value: insertValue === '' ? null : insertValue,
        narrator: insertNarrator === '' ? null : insertNarrator,
        timestamp: insertUtc,
      });
      setInsertResult(res.data);
      await load();
    } catch (err) {
      setError(`เพิ่ม log ไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setInsertBusy(false);
    }
  };

  const loadBulkSessions = async () => {
    const id = parseInt(bulkVideoId, 10);
    if (!Number.isInteger(id)) {
      setError('กรุณาใส่หมายเลขวิดีโอสำหรับ bulk add');
      return;
    }
    try {
      const res = await getVideoSessions(id, bulkNarrator);
      const list = res.data.sessions || [];
      setBulkSessions(list);
      setBulkStartId(list[0] ? String(list[0].start_event_id) : '');
    } catch (err) {
      setError(`โหลดเซสชันไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    }
  };

  const submitBulk = async () => {
    const startEventId = parseInt(bulkStartId, 10);
    if (!Number.isInteger(startEventId)) {
      setError('เลือกเซสชันก่อน');
      return;
    }
    const events = bulkRows.map((r) => ({
      action: r.action,
      offset_seconds: parseFloat(r.offset_seconds),
      value: r.value === '' ? null : r.value,
    }));
    setBulkBusy(true);
    setBulkResult(null);
    setError(null);
    try {
      const res = await bulkCreateLogs({
        startEventId,
        events,
        reprocess: bulkReprocess,
      });
      setBulkResult(res.data);
      await load();
    } catch (err) {
      setError(`bulk add ไม่สำเร็จ: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBulkBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  if (!isAdmin) {
    return (
      <div>
        <Link to="/">&laquo; Back to Video List</Link>
        <Alert variant="info" className="mt-3">เฉพาะผู้ดูแลเท่านั้นที่ดู event log ได้</Alert>
      </div>
    );
  }

  return (
    <div className="event-log-page">
      <Link to="/">&laquo; Back to Video List</Link>
      <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
        <h2 className="mb-0">Event log</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" onClick={openInsert}>เพิ่ม log</Button>
          <Button onClick={openBulk}>Bulk add markers</Button>
        </div>
      </div>

      <Form
        className="row g-2 align-items-end mt-2"
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
      >
        <div className="col-6 col-md-2">
          <Form.Label className="small mb-1">Video id</Form.Label>
          <Form.Control
            type="number"
            value={videoId}
            onChange={(e) => setFilter('video_id', e.target.value)}
            aria-label="Filter video id"
          />
        </div>
        <div className="col-6 col-md-2">
          <Form.Label className="small mb-1">Action</Form.Label>
          <Form.Select value={action} onChange={(e) => setFilter('action', e.target.value)} aria-label="Filter action">
            <option value="">ทั้งหมด</option>
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </Form.Select>
        </div>
        <div className="col-6 col-md-2">
          <Form.Label className="small mb-1">Narrator</Form.Label>
          <Form.Select value={narrator} onChange={(e) => setFilter('narrator', e.target.value)} aria-label="Filter narrator">
            <option value="">ทั้งหมด</option>
            <option value="Won">ว่าน</option>
            <option value="Waan">หวาน</option>
          </Form.Select>
        </div>
        <div className="col-6 col-md-2">
          <Form.Label className="small mb-1">Time ≥</Form.Label>
          <Form.Control
            type="datetime-local"
            step="1"
            value={timestampGte}
            onChange={(e) => setFilter('timestamp_gte', e.target.value)}
          />
        </div>
        <div className="col-6 col-md-2">
          <Form.Label className="small mb-1">Time ≤</Form.Label>
          <Form.Control
            type="datetime-local"
            step="1"
            value={timestampLte}
            onChange={(e) => setFilter('timestamp_lte', e.target.value)}
          />
        </div>
        <div className="col-3 col-md-1">
          <Form.Label className="small mb-1">Id ≥</Form.Label>
          <Form.Control type="number" value={idGte} onChange={(e) => setFilter('id_gte', e.target.value)} />
        </div>
        <div className="col-3 col-md-1">
          <Form.Label className="small mb-1">Id ≤</Form.Label>
          <Form.Control type="number" value={idLte} onChange={(e) => setFilter('id_lte', e.target.value)} />
        </div>
      </Form>

      {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
      {loading && <div className="mt-3"><LoadingSpinner /></div>}

      {!loading && (
        <>
          <p className="small text-muted mt-3 mb-1">{total} รายการ</p>
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Id</th>
                <th>Video</th>
                <th>Action</th>
                <th>Value</th>
                <th>Narrator</th>
                <th>Time</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={7} className="text-muted">ไม่พบล็อก</td></tr>
              ) : items.map((log) => (
                <tr key={log.id}>
                  <td>{log.id}</td>
                  <td>
                    <Link to={`/videos/${log.video_id}`}>{log.video_id}</Link>
                  </td>
                  <td>
                    {log.action}
                    {log.deactivated ? <Badge bg="secondary" className="ms-1">deactivated</Badge> : null}
                  </td>
                  <td className="small">{log.value ?? '—'}</td>
                  <td>{NARRATOR_LABEL[log.narrator] || log.narrator || '—'}</td>
                  <td className="small">{formatDateTime(log.timestamp)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      disabled={deletingId === log.id}
                      onClick={() => onDeleteClick(log)}
                    >
                      ลบ
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="d-flex gap-2 align-items-center">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1}
              onClick={() => setFilter('page', String(page - 1))}
            >
              Prev
            </Button>
            <span className="small">หน้า {page} / {totalPages}</span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page >= totalPages}
              onClick={() => setFilter('page', String(page + 1))}
            >
              Next
            </Button>
          </div>
        </>
      )}

      <Modal show={!!confirmDelete} onHide={() => setConfirmDelete(null)}>
        <Modal.Header closeButton>
          <Modal.Title>ลบล็อก #{confirmDelete?.id}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmDelete && (
            <p>
              Action <code>{confirmDelete.action}</code> อาจทำให้เซสชัน pairing พัง —
              ถ้าต้องการลบทั้งเซสชัน ใช้หน้า Sessions แทน
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)}>ยกเลิก</Button>
          <Button
            variant="danger"
            disabled={deletingId === confirmDelete?.id}
            onClick={() => confirmDelete && doDelete(confirmDelete)}
          >
            ลบ
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showInsert} onHide={() => !insertBusy && setShowInsert(false)}>
        <Modal.Header closeButton>
          <Modal.Title>เพิ่ม log ทีละแถว</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            เขียนแถวเดียวลงตารางตรง ๆ ไม่มีการตรวจสอบ pairing —
            ใช้ตอนที่ log หายไป เช่นเซสชันที่ไม่มี <code>stop_record</code>
          </p>
          <div className="row g-2">
            <div className="col-6">
              <Form.Label className="small mb-1">Video id</Form.Label>
              <Form.Control
                type="number"
                value={insertVideoId}
                onChange={(e) => setInsertVideoId(e.target.value)}
                aria-label="Insert video id"
              />
            </div>
            <div className="col-6">
              <Form.Label className="small mb-1">Action</Form.Label>
              <Form.Select
                value={insertAction}
                onChange={(e) => setInsertAction(e.target.value)}
                aria-label="Insert action"
              >
                {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
              </Form.Select>
            </div>
            <div className="col-6">
              <Form.Label className="small mb-1">Narrator</Form.Label>
              <Form.Select
                value={insertNarrator}
                onChange={(e) => setInsertNarrator(e.target.value)}
                aria-label="Insert narrator"
              >
                <option value="">—</option>
                <option value="Won">ว่าน</option>
                <option value="Waan">หวาน</option>
              </Form.Select>
            </div>
            <div className="col-6">
              <Form.Label className="small mb-1">Value</Form.Label>
              <Form.Control
                value={insertValue}
                onChange={(e) => setInsertValue(e.target.value)}
                placeholder="optional"
                aria-label="Insert value"
              />
            </div>
            <div className="col-12">
              <Form.Label className="small mb-1">Time (เวลาเครื่องคุณ)</Form.Label>
              <Form.Control
                type="datetime-local"
                step="0.001"
                value={insertLocal}
                onChange={(e) => setInsertLocal(e.target.value)}
                aria-label="Insert timestamp"
              />
              <div className="small text-muted mt-1">
                {insertUtc ? <>เก็บลงฐานข้อมูลเป็น UTC: <code>{insertUtc}</code></> : 'เวลาไม่ถูกต้อง'}
              </div>
            </div>
          </div>

          {insertResult && (
            <Alert variant="success" className="mt-3 mb-0">
              เพิ่มแล้ว #{insertResult.id} — {insertResult.action} @ {formatDateTime(insertResult.timestamp)}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={insertBusy} onClick={() => setShowInsert(false)}>ปิด</Button>
          <Button variant="primary" disabled={insertBusy || !insertUtc} onClick={submitInsert}>
            {insertBusy ? 'กำลังบันทึก…' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showBulk} onHide={() => !bulkBusy && setShowBulk(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Bulk add play / pause / seek</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-4">
              <Form.Label className="small mb-1">Video id</Form.Label>
              <Form.Control
                type="number"
                value={bulkVideoId}
                onChange={(e) => setBulkVideoId(e.target.value)}
              />
            </div>
            <div className="col-4">
              <Form.Label className="small mb-1">Narrator</Form.Label>
              <Form.Select value={bulkNarrator} onChange={(e) => setBulkNarrator(e.target.value)}>
                <option value="Won">ว่าน</option>
                <option value="Waan">หวาน</option>
              </Form.Select>
            </div>
            <div className="col-4">
              <Button variant="outline-primary" onClick={loadBulkSessions}>โหลดเซสชัน</Button>
            </div>
            <div className="col-12">
              <Form.Label className="small mb-1">Session</Form.Label>
              <Form.Select
                value={bulkStartId}
                onChange={(e) => setBulkStartId(e.target.value)}
                aria-label="Bulk session"
              >
                <option value="">—</option>
                {bulkSessions.map((s) => (
                  <option key={s.start_event_id} value={s.start_event_id}>
                    #{s.start_event_id} {formatDateTime(s.start)}
                    {s.duration != null ? ` (${s.duration}s)` : ''}
                    {s.filename ? ` — ${s.filename}` : ''}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>

          <Table size="sm" bordered>
            <thead>
              <tr>
                <th>Action</th>
                <th>Offset (s)</th>
                <th>Value (position)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {bulkRows.map((row, i) => (
                <tr key={i}>
                  <td>
                    <Form.Select
                      value={row.action}
                      onChange={(e) => {
                        const next = [...bulkRows];
                        next[i] = { ...next[i], action: e.target.value };
                        setBulkRows(next);
                      }}
                    >
                      <option value="play">play</option>
                      <option value="pause">pause</option>
                      <option value="seek">seek</option>
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.1"
                      value={row.offset_seconds}
                      onChange={(e) => {
                        const next = [...bulkRows];
                        next[i] = { ...next[i], offset_seconds: e.target.value };
                        setBulkRows(next);
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      value={row.value}
                      onChange={(e) => {
                        const next = [...bulkRows];
                        next[i] = { ...next[i], value: e.target.value };
                        setBulkRows(next);
                      }}
                      placeholder={row.action === 'pause' ? 'optional' : 'required'}
                    />
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={bulkRows.length === 1}
                      onClick={() => setBulkRows(bulkRows.filter((_, j) => j !== i))}
                    >
                      ×
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Button size="sm" variant="outline-primary" className="mb-3" onClick={() => setBulkRows([...bulkRows, emptyBulkRow()])}>
            + แถว
          </Button>

          <Form.Check
            type="checkbox"
            id="bulk-reprocess"
            label="Reprocess audio หลังบันทึก (default on)"
            checked={bulkReprocess}
            onChange={(e) => setBulkReprocess(e.target.checked)}
          />

          {bulkResult && (
            <Alert variant={bulkResult.errors?.length ? 'warning' : 'success'} className="mt-3 mb-0">
              สร้างได้ {bulkResult.created?.length || 0} รายการ
              {bulkResult.errors?.length > 0 && (
                <ul className="mb-0 mt-1 small">
                  {bulkResult.errors.map((err) => (
                    <li key={err.index}>แถว {err.index}: {err.detail}</li>
                  ))}
                </ul>
              )}
              {bulkResult.reprocess && (
                <div className="small mt-1">
                  Reprocess: {bulkResult.reprocess.success ? 'สำเร็จ' : `ล้มเหลว — ${bulkResult.reprocess.message}`}
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" disabled={bulkBusy} onClick={() => setShowBulk(false)}>ปิด</Button>
          <Button variant="primary" disabled={bulkBusy} onClick={submitBulk}>
            {bulkBusy ? 'กำลังบันทึก…' : 'บันทึก'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EventLogPage;
